/**
 * The individual developer page.
 *
 * This is the drilldown the demo was missing: "Priya gives 32% of the reviews"
 * -> click -> here is what that actually looks like for her. For the EM
 * persona that is the most persuasive moment in the product, and it lands as a
 * workload problem to redistribute rather than a verdict on a person.
 *
 * ## No ranking, deliberately
 *
 * `DeveloperAnalytics` carries five OPTIONAL `TeamComparison` fields, and
 * `MetricCardWithTrend` renders "Team avg: X - Rank #N of M" only under
 * `{teamComparison && ...}`. This generator omits all five, and the sentiment
 * profile omits its two `team_percentile_*` fields, so the demo shows none of
 * it - no rank, no percentile, no vs-team line.
 *
 * That is not a fixture hiding a capability. The product gates cross-person
 * comparison behind an explicit org opt-in precisely because ranking people is
 * the reading we reject, and a demo is the last place to lead with it. Absent
 * optional fields is also exactly what a real org without enough comparable
 * history looks like.
 *
 * ## Why every endpoint is answered, even the thin ones
 *
 * Eight of this page's eleven calls are `.catch(() => null)`, so an uncovered
 * one does not break the page - it silently goes to the NETWORK, carrying a
 * fictional org id to the real backend. The demo's guarantee is that nothing
 * leaves the browser, so thin-but-honest beats falling through.
 */

import type { DeveloperWithMetrics } from '@/types/core';
import type { DeveloperAnalytics, DistributionBucket, RepositoryContributionsResponse } from '@/types/developer';
import type { LanguageBreakdownResponse, PRActivityBreakdown, TestCoverageMetrics } from '@/types/metrics';
import type { ActiveBranchWorkItem, PullRequestListResponse, WorkBreakdownResponse } from '@/types/pr';
import type { DeveloperSentimentProfile, WeeklySentimentDataPoint } from '@/types/review';
import {
  DEMO_ORG_ID,
  DEMO_REPOS,
  findDemoDeveloper,
  resolveProfile,
  type DemoDeveloper,
} from '../fixture';
import { demoDate, demoTimestamp, jitter, rngFor, type DemoSeedInput } from '../seed';
import { buildDemoDeveloperMetrics } from './developer';
import { buildDemoPullRequests } from './pr';

export interface DemoDeveloperDetailParams {
  developerId: string;
  days: number;
  excludeBots: boolean;
}

const seedInput = (params: DemoDeveloperDetailParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.developerId,
  teamId: null,
  excludeBots: params.excludeBots,
});

/** Repos this person contributes to, most active first. */
function reposFor(dev: DemoDeveloper) {
  return DEMO_REPOS.filter((r) => r.profile.contributorIds.includes(dev.id)).sort(
    (a, b) =>
      a.profile.contributorIds.indexOf(dev.id) - b.profile.contributorIds.indexOf(dev.id)
  );
}

/**
 * One developer's record, taken from the SAME generator the league table uses.
 *
 * Deriving it rather than writing a second source is the point: a prospect who
 * clicks from the league into a profile must not find different numbers for the
 * same person.
 */
export function buildDemoDeveloper(params: DemoDeveloperDetailParams): DeveloperWithMetrics | undefined {
  const cohort = buildDemoDeveloperMetrics({
    days: params.days,
    repoId: null,
    teamId: null,
    excludeBots: params.excludeBots,
  });
  return cohort.developers.find((d) => d.id === params.developerId);
}

function buckets(
  labels: string[],
  weights: number[],
  total: number,
  rng: () => number
): DistributionBucket[] {
  const jittered = weights.map((w) => Math.max(0.01, w * jitter(rng, 0.15)));
  const sum = jittered.reduce((a, b) => a + b, 0);
  return labels.map((range_label, i) => {
    const share = jittered[i] / sum;
    return {
      range_label,
      count: Math.round(total * share),
      percentage: Number((share * 100).toFixed(1)),
    };
  });
}

export function buildDemoDeveloperAnalytics(params: DemoDeveloperDetailParams): DeveloperAnalytics {
  const dev = findDemoDeveloper(params.developerId);
  const repos = dev ? reposFor(dev) : [];
  const profile = repos.length > 0 ? repos[0].profile : resolveProfile(null, null);
  const rng = rngFor(seedInput(params, 'developer/analytics'), 'distributions');

  // Same record, same count: the distribution has to bucket the PRs the rest of
  // the page says this person merged.
  const prs = prCounts(params).merged;

  // Skewed toward small PRs, with a visible long tail - which is what a real
  // distribution looks like, and what makes the chart worth showing at all.
  const sizeWeights = profile.avgPrSize > 300 ? [18, 26, 24, 20, 12] : [34, 30, 20, 11, 5];
  const slow = profile.cycleTimeHours > 55;
  const timeWeights = slow ? [14, 22, 26, 24, 14] : [32, 30, 20, 12, 6];

  return {
    developer_id: params.developerId,
    organization_id: DEMO_ORG_ID,
    pr_size_distribution: buckets(
      ['1-50', '51-150', '151-400', '401-1000', '1000+'],
      sizeWeights,
      prs,
      rng
    ),
    cycle_time_distribution: buckets(
      ['<4h', '4-24h', '1-3d', '3-7d', '7d+'],
      timeWeights,
      prs,
      rng
    ),
    // The five TeamComparison fields are omitted on purpose - see the file
    // header. Their absence is what keeps rank and percentile off the page.
  };
}

export function buildDemoDeveloperPullRequests(
  params: DemoDeveloperDetailParams
): PullRequestListResponse {
  const all = buildDemoPullRequests({
    days: params.days,
    repoId: null,
    teamId: null,
    excludeBots: params.excludeBots,
  });
  const mine = all.pull_requests.filter((pr) => pr.author_id === params.developerId);
  return {
    ...all,
    pull_requests: mine,
    total: mine.length,
    page_size: Math.max(mine.length, 1),
  };
}

/**
 * PR counts for this person, read from their ONE record.
 *
 * Not recomputed. The first version derived these from the repo profile with
 * its own formula, and the page rendered "36 Merged PRs" in one card and "PRs
 * Merged 44" in another - two numbers for the same thing, a few hundred pixels
 * apart. Reading the record makes that class of contradiction impossible
 * rather than merely unlikely.
 */
function prCounts(params: DemoDeveloperDetailParams) {
  const record = buildDemoDeveloper(params);
  const merged = Math.max(1, Math.round(record?.metrics.prs_merged?.value ?? 8));
  const created = Math.max(merged, Math.round(record?.metrics.prs_created?.value ?? merged));
  return {
    merged,
    // "In progress" is what was opened but not yet merged in the window.
    inProgress: Math.max(0, created - merged),
    avgPrSize: record?.metrics.avg_lines_per_pr?.value ?? 200,
  };
}

export function buildDemoDeveloperTestCoverage(
  params: DemoDeveloperDetailParams
): TestCoverageMetrics {
  const dev = findDemoDeveloper(params.developerId);
  const repos = dev ? reposFor(dev) : [];
  const profile = repos.length > 0 ? repos[0].profile : resolveProfile(null, null);
  const rng = rngFor(seedInput(params, 'developer/test-coverage'), 'coverage');

  const { merged: prsMerged, inProgress, avgPrSize } = prCounts(params);
  const appLoc = Math.round(prsMerged * avgPrSize * 0.72 * jitter(rng, 0.15));
  // Test ratio tracks the repo's own quality profile, so someone working in the
  // repo with a 13% failure rate does not appear to be writing the most tests.
  const testLoc = Math.round(appLoc * (0.42 - profile.testFailureRateBase * 0.016) * jitter(rng, 0.2));

  return {
    developer_id: params.developerId,
    time_period: `${params.days}d`,
    test_loc: Math.max(0, testLoc),
    app_loc: appLoc,
    test_coverage_ratio: Number((Math.max(0, testLoc) / Math.max(appLoc, 1)).toFixed(3)),
    unique_files_touched: Math.round(prsMerged * 3.4 * jitter(rng, 0.2)),
    // Distinct languages across the repos this person works in - NOT a made-up
    // count. It was `repos.length + 1`, which billed Priya as "Bilingual - 2
    // programming languages" directly above a distribution reading
    // "TypeScript 100.0%". She works in one repo, in one language; a focused
    // frontend lead is a perfectly ordinary profile, and saying so is better
    // than flattering the fixture.
    language_diversity: new Set(repos.map((r) => r.repository.language)).size || 1,
    prs_merged: prsMerged,
    prs_in_progress: inProgress,
  };
}

/** Languages follow the repos the person actually works in. */
export function buildDemoDeveloperLanguages(
  params: DemoDeveloperDetailParams
): LanguageBreakdownResponse {
  const dev = findDemoDeveloper(params.developerId);
  const repos = dev ? reposFor(dev) : [];
  const rng = rngFor(seedInput(params, 'developer/languages'), 'languages');
  const coverage = buildDemoDeveloperTestCoverage(params);

  const EXTENSIONS: Record<string, string[]> = {
    TypeScript: ['.ts', '.tsx'],
    Go: ['.go'],
    Python: ['.py'],
    HCL: ['.tf'],
  };

  const weights = repos.map((_, i) => Math.max(0.05, 1 / (i + 1.3)) * jitter(rng, 0.15));
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const totalLoc = coverage.app_loc + coverage.test_loc;

  const byLanguage = new Map<string, number>();
  repos.forEach((repo, i) => {
    const language = repo.repository.language ?? 'TypeScript';
    byLanguage.set(language, (byLanguage.get(language) ?? 0) + (weights[i] / sum) * totalLoc);
  });

  const languages = [...byLanguage.entries()]
    .map(([language, loc]) => ({
      language,
      extensions: EXTENSIONS[language] ?? [],
      lines_of_code: Math.round(loc),
      percentage: Number(((loc / Math.max(totalLoc, 1)) * 100).toFixed(1)),
    }))
    .sort((a, b) => b.lines_of_code - a.lines_of_code);

  return {
    developer_id: params.developerId,
    time_period: `${params.days}d`,
    total_loc: totalLoc,
    languages,
    prs_merged: coverage.prs_merged,
    prs_in_progress: coverage.prs_in_progress,
  };
}

export function buildDemoDeveloperWorkBreakdown(
  params: DemoDeveloperDetailParams
): WorkBreakdownResponse {
  const rng = rngFor(seedInput(params, 'developer/work-breakdown'), 'categories');
  const coverage = buildDemoDeveloperTestCoverage(params);
  const totalLoc = coverage.app_loc + coverage.test_loc;

  const raw: Array<[WorkBreakdownResponse['categories'][number]['category'], number]> = [
    ['code', 58],
    ['test', 24],
    ['config', 9],
    ['docs', 6],
    ['data', 3],
  ];
  const jittered = raw.map(([, w]) => w * jitter(rng, 0.12));
  const sum = jittered.reduce((a, b) => a + b, 0);

  return {
    developer_id: params.developerId,
    time_period: `${params.days}d`,
    total_loc: totalLoc,
    categories: raw.map(([category], i) => ({
      category,
      lines_of_code: Math.round((jittered[i] / sum) * totalLoc),
      percentage: Number(((jittered[i] / sum) * 100).toFixed(1)),
    })),
    prs_merged: coverage.prs_merged,
    prs_in_progress: coverage.prs_in_progress,
  };
}

export function buildDemoDeveloperPRActivity(
  params: DemoDeveloperDetailParams
): PRActivityBreakdown {
  const coverage = buildDemoDeveloperTestCoverage(params);
  return {
    developer_id: params.developerId,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    prs_merged: coverage.prs_merged,
    prs_in_progress: coverage.prs_in_progress,
    total_lines_of_code: coverage.app_loc + coverage.test_loc,
  };
}

export function buildDemoDeveloperRepositoryContributions(
  params: DemoDeveloperDetailParams
): RepositoryContributionsResponse {
  const dev = findDemoDeveloper(params.developerId);
  const repos = dev ? reposFor(dev) : [];
  const rng = rngFor(seedInput(params, 'developer/repo-contributions'), 'contributions');
  const coverage = buildDemoDeveloperTestCoverage(params);

  const weights = repos.map((_, i) => Math.max(0.05, 1 / (i + 1.3)));
  const sum = weights.reduce((a, b) => a + b, 0) || 1;

  return {
    developer_id: params.developerId,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    contributions: repos.map((repo, i) => {
      const share = weights[i] / sum;
      return {
        repository_id: repo.repository.id,
        repository_name: repo.repository.name,
        repository_full_name: repo.repository.full_name,
        prs_merged: Math.max(1, Math.round(coverage.prs_merged * share)),
        lines_of_code: Math.round((coverage.app_loc + coverage.test_loc) * share),
        reviews_given: Math.max(
          0,
          Math.round((dev?.reviewGiveWeight ?? 0) * share * (params.days / 30) * jitter(rng, 0.15))
        ),
      };
    }),
    total_repositories: repos.length,
  };
}

export function buildDemoDeveloperActiveBranches(
  params: DemoDeveloperDetailParams
): ActiveBranchWorkItem[] {
  const dev = findDemoDeveloper(params.developerId);
  const repos = dev ? reposFor(dev) : [];
  if (repos.length === 0) return [];
  const rng = rngFor(seedInput(params, 'developer/active-branches'), 'branches');

  // One or two in-flight branches, not a wall of them: this card is "work that
  // has not reached a PR yet", and a long list would read as a process problem
  // the rest of the fixture does not claim.
  return repos.slice(0, 2).map((repo, i) => {
    const additions = Math.round(repo.profile.avgPrSize * 0.6 * jitter(rng, 0.3));
    const deletions = Math.round(repo.profile.avgPrSize * 0.2 * jitter(rng, 0.4));
    return {
      branch_name: i === 0 ? 'spike/retry-budget' : 'chore/drop-legacy-shim',
      repository_id: repo.repository.id,
      repository_name: repo.repository.name,
      repository_full_name: repo.repository.full_name,
      total_loc: additions + deletions,
      additions,
      deletions,
      last_commit_at: demoTimestamp(18 + i * 26),
    };
  });
}

/**
 * Review-comment sentiment.
 *
 * `team_percentile_given` / `team_percentile_received` are omitted for the same
 * reason as the `TeamComparison` fields: `SentimentCard` only renders a
 * percentile when one is supplied.
 */
export function buildDemoDeveloperSentiment(
  params: DemoDeveloperDetailParams
): DeveloperSentimentProfile {
  const dev = findDemoDeveloper(params.developerId);
  const rng = rngFor(seedInput(params, 'developer/sentiment'), 'profile');
  const scale = (dev?.reviewGiveWeight ?? 4) * (params.days / 30);

  const given = Math.max(1, Math.round(scale * 2.4 * jitter(rng, 0.15)));
  const received = Math.max(1, Math.round((dev?.reviewReceiveWeight ?? 6) * (params.days / 30) * 1.8 * jitter(rng, 0.15)));

  const split = (total: number, weights: [number, number, number, number]) => {
    const sum = weights.reduce((a, b) => a + b, 0);
    return {
      positive: Math.round((weights[0] / sum) * total),
      constructive: Math.round((weights[1] / sum) * total),
      neutral: Math.round((weights[2] / sum) * total),
      negative: Math.round((weights[3] / sum) * total),
    };
  };

  return {
    developer_id: params.developerId,
    external_login: dev?.login,
    name: dev?.name,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    avg_sentiment_given: Number((0.42 * jitter(rng, 0.2)).toFixed(2)),
    sentiment_breakdown_given: split(given, [34, 44, 20, 2]),
    category_breakdown_given: [
      { category: 'Correctness', count: Math.round(given * 0.36), percentage: 36 },
      { category: 'Readability', count: Math.round(given * 0.27), percentage: 27 },
      { category: 'Testing', count: Math.round(given * 0.21), percentage: 21 },
      { category: 'Style', count: Math.round(given * 0.16), percentage: 16 },
    ],
    total_comments_given: given,
    avg_comment_length: Math.round(118 * jitter(rng, 0.2)),
    actionable_ratio: Number((0.68 * jitter(rng, 0.1)).toFixed(2)),
    avg_sentiment_received: Number((0.39 * jitter(rng, 0.2)).toFixed(2)),
    sentiment_breakdown_received: split(received, [31, 46, 21, 2]),
    total_comments_received: received,
    // team_percentile_* omitted on purpose - see the doc comment above.
  };
}

export function buildDemoDeveloperSentimentTrends(
  params: DemoDeveloperDetailParams
): WeeklySentimentDataPoint[] {
  const rng = rngFor(seedInput(params, 'developer/sentiment-trends'), 'weekly');
  const dev = findDemoDeveloper(params.developerId);

  return Array.from({ length: 12 }, (_, i) => {
    const weeksAgo = 11 - i;
    return {
      week_start: demoDate(weeksAgo * 7),
      avg_sentiment_given: Number((0.42 * jitter(rng, 0.25)).toFixed(2)),
      avg_sentiment_received: Number((0.39 * jitter(rng, 0.25)).toFixed(2)),
      comments_given_count: Math.max(0, Math.round((dev?.reviewGiveWeight ?? 4) * 0.5 * jitter(rng, 0.4))),
      comments_received_count: Math.max(0, Math.round((dev?.reviewReceiveWeight ?? 6) * 0.4 * jitter(rng, 0.4))),
    };
  });
}
